import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export interface FilterChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Whether this chip's filter is currently applied. */
  active?: boolean;
  label: string;
}

/**
 * FilterChip — the one blessed filter/segment pill. Active chips take the
 * copper fill (same finish as primary actions); inactive chips sit quietly
 * on the linen. Replaces the per-page hand-rolled copies.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ className, active = false, label, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        className={cn(
          "inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium",
          "transition-[background-color,color,box-shadow] duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-body",
          "disabled:cursor-not-allowed disabled:opacity-50",
          active
            ? "bg-copper text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(50_30_12/0.18)]"
            : "bg-bg-muted text-text-secondary ring-1 ring-inset ring-border-strong/40 hover:bg-bg-hover hover:text-text-primary",
          className,
        )}
        {...props}
      >
        {label}
      </button>
    );
  },
);

FilterChip.displayName = "FilterChip";
